import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { ChevronDown, Check, Settings2 } from 'lucide-react';
import { media } from '../../styles/breakpoints';

const SwitcherWrapper = styled.div`
  position: relative;
  min-width: 0;
`;

// $current: the page works on this instance (blue); otherwise neutral.
const InstanceSelector = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: ${props => (props.$current ? '#e8f4ff' : '#aaa')};
  background: ${props => (props.$current ? 'rgba(0, 153, 255, 0.12)' : 'rgba(255, 255, 255, 0.04)')};
  border: 1px solid ${props => (props.$current ? 'rgba(0, 153, 255, 0.35)' : 'rgba(255, 255, 255, 0.1)')};
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 9px;
  transition: all 0.15s ease;
  max-width: 220px;

  /* The top bar also holds the stage selector here (AdminHeader TOP_STAGES_MIN) */
  @media (min-width: 1250px) and (max-width: 1439px) { max-width: 170px; }

  ${media.sm} {
    max-width: 160px;
    font-size: 13px;
    padding: 6px 10px;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    background: ${props => (props.$current ? 'rgba(0, 153, 255, 0.18)' : 'rgba(255, 255, 255, 0.07)')};
    color: #fff;
  }

  svg {
    opacity: 0.8;
    flex-shrink: 0;
    transition: transform 0.15s ease;
  }

  &[data-open="true"] svg {
    transform: rotate(180deg);
  }
`;

// Socket connection for the instance: green connected, red not.
const ConnectionDot = styled.i`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${props => (props.$connected ? '#00ff88' : '#ff4444')};
  box-shadow: 0 0 6px ${props => (props.$connected ? 'rgba(0, 255, 136, 0.5)' : 'rgba(255, 68, 68, 0.5)')};
`;

const InstanceDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 10px;
  min-width: 280px;
  max-height: 400px;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  z-index: 200;
  animation: slideDown 0.15s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const InstanceDropdownHeader = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #333;
  font-size: 11px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const InstanceOption = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: ${props => props.$active ? 'rgba(0, 153, 255, 0.1)' : 'transparent'};
  border: none;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;

  &:hover {
    background: rgba(0, 153, 255, 0.15);
  }
`;

const InstanceOptionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const InstanceOptionName = styled.span`
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const InstanceOptionId = styled.span`
  font-size: 11px;
  color: #666;
  font-family: monospace;
`;

const ManageLink = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: transparent;
  border: none;
  border-top: 1px solid #333;
  color: #0099ff;
  font-size: 13px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: rgba(0, 153, 255, 0.1);
  }
`;

/**
 * InstanceSwitcher - the instance chip and drop-down in the admin header.
 * The header owns the instance list and what selecting one does.
 */
const InstanceSwitcher = ({ instanceId, label, instances, isConnected, current, onSelect }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (id) => {
    setOpen(false);
    onSelect(id);
  };

  const handleManage = () => {
    setOpen(false);
    navigate('/admin/instances');
  };

  const title = instanceId ? (isConnected ? 'Yhdistetty' : 'Ei yhteyttä') : undefined;

  return (
    <SwitcherWrapper ref={wrapperRef}>
      <InstanceSelector
        data-open={open}
        $current={current}
        title={title}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
      >
        {instanceId && <ConnectionDot $connected={isConnected} aria-hidden="true" />}
        <span>{label}</span>
        <ChevronDown size={14} />
      </InstanceSelector>

      {open && (
        <InstanceDropdown>
          <InstanceDropdownHeader>Vaihda instanssia</InstanceDropdownHeader>
          {instances.map((inst) => (
            <InstanceOption
              key={inst.id}
              $active={inst.id === instanceId}
              onClick={() => handleSelect(inst.id)}
            >
              <InstanceOptionInfo>
                <InstanceOptionName>{inst.title || inst.name}</InstanceOptionName>
                <InstanceOptionId>{inst.id.substring(0, 8)}...</InstanceOptionId>
              </InstanceOptionInfo>
              {inst.id === instanceId && <Check size={16} style={{ color: '#0099ff' }} />}
            </InstanceOption>
          ))}
          <ManageLink onClick={handleManage}>
            <Settings2 size={14} />
            Hallitse instansseja…
          </ManageLink>
        </InstanceDropdown>
      )}
    </SwitcherWrapper>
  );
};

export default InstanceSwitcher;
